<template>
  <div>
    <app-cells><h1 class="title">POS transactions</h1></app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onSearch"
          v-model="search"
          type="search"
          placeholder="Search"
          name="search"
        />
      </app-cells>
      <div v-if="!pos_transactions.length" class="no-results">
        <img src="@/assets/img/schedule-plug-icon.svg" alt="">
        <div class="no-results__title">No POS transactions</div>
      </div>
      <template v-else>
        <div class="info-cards">
          <label
            v-for="transaction of pos_transactions"
            :key="transaction.id"
            class="info-cards__card"
          >
            <input type="checkbox" v-model="checked_items" :value="transaction">
            <span class="info-cards__row"><span>Date:</span><span>{{ dateTimeFormat(transaction.created_at) }}</span></span>
            <span v-if="transaction.reservation" class="info-cards__row">
              <span>Guest:</span><span>{{ transaction.reservation.group_name }}</span>
            </span>
            <span v-if="transaction.reservation_room?.room" class="info-cards__row">
              <span>Room:</span><span>{{ transaction.reservation_room.room.short_name }}</span>
            </span>
            <span v-if="transaction.house_account" class="info-cards__row">
              <span>House Account:</span><span>{{ transaction.house_account.account_name }}</span>
            </span>
            <span class="info-cards__row"><span>Amount:</span><span>{{ formatString(transaction.amount) }} USD</span></span>
          </label>
        </div>
      </template>
      <app-cells v-if="pagination.pages > 1" position="center">
        <paginate
          :clickHandler="onClickPagination"
          v-model="pagination.page"
          :page-count="pagination.pages"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'pagination__item'"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :prev-link-class="'pagination__link pagination__link--prev'"
        />
      </app-cells>
      <div class="flying-block" :class="{ 'flying-block--show': is_show_flying_block }">
        <div class="flying-block__text">Transactions: <span>{{ checked_items.length }} ({{ checkedSum }} USD) </span></div>
        <app-button @click.native="onShowModal" ref="allocate" size="small">Attach</app-button>
      </div>
    </template>
    <modal
      name="receipt-modal"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
    >
      <div class="popup">
        <button @click="$modal.hide('receipt-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--left">Amount: {{ checkedSum }} USD</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Amount ($)" label-for="amount" required>
              <app-fin-number
                v-model="form.amount"
                :error="$v.form.amount.$error"
                id="amount"
                name="amount"
                placeholder="0"
                inputmode="decimal"
              />
              <template #error>
                <div v-if="$v.form.amount.$dirty && !$v.form.amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Bank" required>
              <app-cells :indent="false">
                <app-radio
                  v-for="radio of bank_options"
                  :key="radio.id"
                  v-model="form.bank"
                  :value="radio.id"
                  name="filterBank"
                  :checked="form.bank === radio.id"
                >
                  <template #label>{{ radio.short_name }}</template>
                </app-radio>
              </app-cells>
            </app-form-group>
            <app-form-group label="Channel" required>
              <app-cells :indent="false">
                <app-radio
                  v-for="radio of channel_options"
                  :key="radio.code"
                  v-model="form.channel"
                  :value="radio.code"
                  name="filterCreditCard"
                  :checked="form.channel === radio.code"
                >
                  <template #label>{{ radio.name }}</template>
                </app-radio>
              </app-cells>
            </app-form-group>
            <app-form-group label="RRN" required label-for="form_rrn">
              <app-input
                v-model="form.rrn"
                :error="$v.form.rrn.$error"
                id="form_rrn"
                name="form_rrn"
                placeholder="RRN"
                type="number"
                inputmode="numeric"
              />
              <template #error>
                <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.required">Required field</div>
                <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.maxLength">Length of RRN must be equal to 12</div>
                <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.minLength">Length of RRN must be equal to 12</div>
              </template>
            </app-form-group>
            <app-form-group label="Machine slip" required>
              <app-uploader
                v-model="form.machine_slip"
                :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
              />
              <template #error>
                <div v-if="$v.form.machine_slip.$dirty && !$v.form.machine_slip.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit">Add</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { getBanks, getCardTypes, getHotelManagementTransactions, postHotelManagementTransaction } from '@/http'
import { required, maxLength, minLength } from 'vuelidate/lib/validators'
import { debounce } from 'lodash'

export default {
  name: 'HotelManagementPOSTransactions',
  data() {
    return {
      page_loaded: false,
      checked_items: [],
      is_show_flying_block: false,
      search: '',
      pos_transactions: [],
      bank_options: [],
      channel_options: [],
      form: {},
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  validations: {
    form: {
      amount: { required },
      rrn: {
        required,
        maxLength: maxLength(12),
        minLength: minLength(12),
      },
      machine_slip: { required },
      bank: { required },
      channel: { required },
    }
  },
  computed: {
    checkedSum() {
      return this.checked_items
        .map(item => Number(item.amount))
        .reduce((a, b) => {
          return a + b
        }, 0)
    },
  },
  watch: {
    checked_items(value) {
      this.is_show_flying_block = value.length > 0
    },
  },
  created() {
    getBanks({pos: true}).then(response => { this.bank_options = response.data })
    getCardTypes().then(response => { this.channel_options = response.data })
    this.fetchItems()
  },
  methods: {
    async fetchItems() {
      const response = await getHotelManagementTransactions({
        page: this.pagination.page,
        page_size: 5,
        pos_category: 'missing',
        search: this.search,
      })
      this.pos_transactions = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.page_loaded = true
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postHotelManagementTransaction({
        ...this.form,
        machine_slip: this.form.machine_slip.id,
        payments: this.checked_items.map(item => item.payment.id)
      })
        .then(() => {
          document.body.scrollIntoView()
          this.search = ''
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$modal.hide('receipt-modal')
          this.checked_items = []
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onShowModal() {
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('receipt-modal')
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    formatString(string) {
      return string ? new Intl.NumberFormat('en-US').format(string) : '-'
    },
    onSearch() {
      this.searchPayment(this)
    },
    searchPayment: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>
